

import { useState, useRef, useEffect, useMemo } from "react"
import { GeoJSON } from 'react-leaflet/GeoJSON';
import { useMapEvent, useMapEvents } from 'react-leaflet/hooks'
import axios from 'axios';
import L from "leaflet";
import {
    LayersControl,
} from 'react-leaflet';
import { divIcon } from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import * as turf from "@turf/turf";
import Table from 'react-bootstrap/Table';
import { DMIRS_MINEDEX_SYMBOLS, minedexIconId, minedexLayerId } from './dmirsSymbols';

import { minedexCommodoties, minedexTargetComodotiy, minedexSiteType } from "./MinedexTypeData"
import {
    Popup,
    Source,
    Layer,
} from 'react-map-gl/maplibre';
const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER
})

const geoserverBase = (process.env.REACT_APP_GOLD_FINDER_GEOSERVER || '').replace(/\/$/, '');

/**
 * MINEDEX sites, served as vector tiles.
 *
 * Replaces a per-viewport WFS GetFeature that returned the whole visible set as
 * GeoJSON. Measured on prod at Kalgoorlie, 1920x1080, the SAME viewport:
 *
 *   display z10.6, WFS GeoJSON   3.59 MB in one blocking request
 *   the same area as z10 tiles   0.65 MB over 24 tiles   (5.5x less)
 *   the same area as z11 tiles   0.59 MB over 66 tiles   (6.1x less)
 *
 * The ratio is that large because GeoJSON repeats every attribute string on
 * every feature while MVT holds one dictionary per tile - and MINEDEX is almost
 * all low-cardinality text (11 commodities, 9 site types, 6 stages, 49 target
 * commodities over 46,770 rows). Tiles are also cached by GWC and the browser,
 * so panning back over ground already seen costs nothing, and filtering is now
 * a style expression over data already in memory instead of a full refetch.
 *
 * WHY THE MINZOOM DROPS FROM 10.6 TO 7.
 *
 * The old 10.6 floor was a WFS budget, not a cartographic choice - below it the
 * payload was unaffordable. Tiles are complete and unfiltered from z7 (a z7
 * tile is 689 KB, z8 363 KB, z12 25 KB), so there is nothing to hide from any
 * more. Unlike soils and drill collars, this layer needs NO density banding at
 * any served zoom, so a client can filter to anything, at any zoom it is drawn,
 * with nothing silently missing. z6 and out are not served at all: no threshold
 * both fits a tile and leaves an honest map. See the style's Abstract.
 */
export const MINEDEX_TILE_LAYER = 'ne:wa_minedex_sites_tiles';
export const MINEDEX_TILE_SOURCE = 'minedex';
export const MINEDEX_TILE_SOURCE_LAYER = 'wa_minedex_sites_tiles';
export const MINEDEX_POINT_MINZOOM = 7;

export const minedexTileUrl =
    `${geoserverBase}/gwc/service/tms/1.0.0/` +
    `${encodeURIComponent(MINEDEX_TILE_LAYER)}@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf`;

/**
 * The MINEDEX record URL for a site.
 *
 * web_link is not carried in the tile. It is verified 100% derivable from
 * site_code across all 46,770 rows, and being unique per row it is the one
 * column that would not dictionary-compress - about 3.6 MB of pure duplication
 * across the state. WFS-sourced features still carry the real column, so that
 * is preferred when present and this is the fallback.
 */
export const minedexWebLink = (props) =>
    props.web_link || (props.site_code
        ? `https://minedex.dmirs.wa.gov.au/Web/common/jump.jsp?jumpType=SITE&id=${props.site_code}`
        : undefined);

/**
 * The client-side equivalent of the filtering the WFS path used to do in JS.
 *
 * DELIBERATELY EXPRESSED AS "HIDE WHAT WAS DESELECTED", NOT "SHOW WHAT WAS
 * SELECTED", which is the opposite of the code this replaces.
 *
 * The old form was `selected.includes(value)`, so any value absent from the
 * hardcoded vocabularies in MinedexTypeData.js was silently invisible. That is
 * live today: 7 sites have a NULL target_com and 3 have a NULL commodity, and
 * none of the ten can be seen on the map at any filter setting.
 *
 * The vocabularies have been stable for 20 months, so this is mostly latent -
 * but MINEDEX is 608 days stale and a refresh is expected to add 1,666 sites.
 * If that refresh introduces a new commodity or target, the "show what was
 * selected" form hides those sites with no error and no clue. This form shows
 * them: a value is hidden only if it is explicitly in the deselected set, so
 * anything unrecognised - including null - falls through visible.
 *
 * `['get', k]` yields null for an attribute the tile does not carry, and null
 * is not a member of any literal list, so the `!in` test passes it. That is the
 * behaviour we want and it needs no special case.
 */
const excludeDeselected = (key, vocabulary, selectedValues) => {
    const selected = new Set(selectedValues);
    const deselected = vocabulary.filter((v) => !selected.has(v));
    if (deselected.length === 0) return null;          // nothing hidden
    return ['!', ['in', ['get', key], ['literal', deselected]]];
};

const MINEDEX_STAGE_FLAGS = [
    ['Proposed', 'siteStageProposed'],
    ['Care and Maintenance', 'siteStageCareAndMaint'],
    ['Shut', 'siteStageShut'],
    ['Operating', 'siteStageOperating'],
    ['Undeveloped', 'siteStageUndeveloped'],
    ['Under Development', 'siteStageUnderDevelopment'],
];

export function buildMinedexTileFilter(minedexFilters) {
    const clauses = [
        excludeDeselected('commodity', minedexCommodoties.map((c) => c.commodityId),
            (minedexFilters.commodityCategory || []).map((e) => e.value)),
        excludeDeselected('target_com', minedexTargetComodotiy,
            (minedexFilters.targetCommodity || []).map((e) => e.value)),
        excludeDeselected('site_type_', minedexSiteType,
            (minedexFilters.siteType || []).map((e) => e.value)),
        excludeDeselected('site_stage', MINEDEX_STAGE_FLAGS.map(([label]) => label),
            MINEDEX_STAGE_FLAGS.filter(([, flag]) => minedexFilters[flag]).map(([label]) => label)),
    ].filter(Boolean);
    return clauses.length ? ['all', ...clauses] : true;
}

function queryMinedexWFS(map, setMineDexGeoJSON, minedexFilters) {
    GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_minedex_sites',
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            bbox: map.getBounds().toBBoxString()
        }
    }).then((e) => {

        const commodityCategories = minedexFilters.commodityCategory.map(e => e.value)

        const targetCommodoties = minedexFilters.targetCommodity.map(e => e.value)

        const siteTypes = minedexFilters.siteType.map(e => e.value);

        e.data.features = e.data.features.filter((feat) => {
            if (commodityCategories.includes(feat.properties.commodity)
                && targetCommodoties.includes(feat.properties.target_com)
                && siteTypes.includes(feat.properties.site_type_)) {

                if ((minedexFilters.siteStageProposed && feat.properties.site_stage == "Proposed")
                    || (minedexFilters.siteStageCareAndMaint && feat.properties.site_stage == "Care and Maintenance")
                    || (minedexFilters.siteStageShut && feat.properties.site_stage == "Shut")
                    || (minedexFilters.siteStageOperating && feat.properties.site_stage == "Operating")
                    || (minedexFilters.siteStageUndeveloped && feat.properties.site_stage == "Undeveloped")
                    || (minedexFilters.siteStageUnderDevelopment && feat.properties.site_stage == "Under Development")) {
                    return true;
                }

            }
        })
        setMineDexGeoJSON(e.data);
    })
};

export function lookupSingleSiteCode(id) {
    const filterQuery = ` 
    <Filter> 
        <PropertyIsEqualTo> 
            <PropertyName>site_code</PropertyName> 
            <Literal>${id}</Literal> 
        </PropertyIsEqualTo> 
    </Filter> 
    `;

    return GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_minedex_sites',
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            filter: filterQuery
        }
    })
}


export function MinedexOverlay({ mineDexGeoJSON, setMineDexGeoJSON, selectedFeature, setSelectedFeature, minedexFilters }) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "Mining_Tenements_DMIRS_003",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
        "features": []
    };

    const overlayName = "Minedex Overlay";
    const [display, setDisplay] = useState(false);
    const [selectedPopup, setSelectedPopup] = useState(<></>);

    const map = useMapEvents({
        moveend() {
            if (map.getZoom() >= 11 && display) { // this is at 5km scale
                queryMinedexWFS(map, setMineDexGeoJSON, minedexFilters);
            } else {
                setMineDexGeoJSON(emptyGeoJson);
                setSelectedPopup(<></>);
            }

        },
        overlayadd(e) {

            if (e.name == overlayName) {
                setDisplay(true);
                queryMinedexWFS(map, setMineDexGeoJSON, minedexFilters);
            }
        },
        overlayremove(e) {
            if (e.name == overlayName) {
                setSelectedPopup(<></>);
                setDisplay(false);
            }
        },
    });

    useEffect(() => {
        if (selectedFeature.type != undefined) {
            const centerPoint = turf.centerOfMass(selectedFeature.geometry);

            setSelectedPopup(
                <Popup autoPan={false} position={{ lat: centerPoint.geometry.coordinates[1], lng: centerPoint.geometry.coordinates[0] }}>
                    <Table striped bordered style={{ marginBottom: 0 }} >
                        <tbody>
                            <tr>
                                <td>Site Code:</td>
                                <td>{selectedFeature.properties.site_code}</td>
                            </tr>
                            <tr>
                                <td>Site Title:</td>
                                <td>{selectedFeature.properties.site_title}</td>
                            </tr>
                            <tr>
                                <td>Site Type:</td>
                                <td>{selectedFeature.properties.site_type_}</td>
                            </tr>
                            <tr>
                                <td>Site Sub Type:</td>
                                <td>{selectedFeature.properties.site_sub_t}</td>
                            </tr>
                            <tr>
                                <td>Site Stage:</td>
                                <td>{selectedFeature.properties.site_stage}</td>
                            </tr>
                            <tr>
                                <td>Commodity:</td>
                                <td>{selectedFeature.properties.commodity}</td>
                            </tr>
                            <tr>
                                <td>Target Commodity:</td>
                                <td>{selectedFeature.properties.target_com}</td>
                            </tr>
                            <tr>
                                <td>Site Commodity:</td>
                                <td>{selectedFeature.properties.site_commo}</td>
                            </tr>
                            <tr>
                                <td>Project Title:</td>
                                <td>{selectedFeature.properties.proj_title}</td>
                            </tr>
                            <tr>
                                <td>Project Code:</td>
                                <td>{selectedFeature.properties.proj_code}</td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ textAlign: 'center' }}>
                                    <a target="_blank" href={minedexWebLink(selectedFeature.properties)}>
                                        Minedex Link
                                    </a>
                                </td>
                            </tr>


                        </tbody>
                    </Table>
                </Popup>
            );

        } else {
            setSelectedPopup(<></>);
        }
    }, [selectedFeature])


    // get a ref to the underlying L.geoJSON
    const geoJsonRef = useRef()

    // set the data to new data whenever it changes
    useEffect(() => {
        if (geoJsonRef.current) {
            geoJsonRef.current.clearLayers()   // remove old data
            geoJsonRef.current.addData(mineDexGeoJSON) // might need to be geojson.features
        }
    }, [geoJsonRef, mineDexGeoJSON])

    useEffect(() => {
        queryMinedexWFS(map, setMineDexGeoJSON, minedexFilters);
    }, [minedexFilters])

    function createMarker(feature, latlng) {


        const iconMarkup = () => {

            // some features have a null value for this... for WHATEVER REASON
            if (feature.properties.commodity === null) {
                feature.properties.commodity = "OTHER (MINEDEX NON-MINERALS)";
            }

            const commodity = minedexCommodoties.find((e) => {
                return e.commodityId === feature.properties.commodity;
            });



            return renderToStaticMarkup(
                <i className={commodity.iconClassName} style={{ color: commodity.color }} />
            );
        }

        const customMarkerIcon = divIcon({
            html: iconMarkup(),
            className: 'dummy'
        });

        return L.marker(latlng, { icon: customMarkerIcon });

    }

    const onEachMinedexFeature = (feature, layer) => {
        layer.on('click', (e) => {
            setSelectedFeature(feature);
        })
    }

    return (
        <div>
            <LayersControl.Overlay name={overlayName}>

                <GeoJSON
                    ref={geoJsonRef}
                    data={mineDexGeoJSON.features}
                    pointToLayer={createMarker}
                    onEachFeature={onEachMinedexFeature}
                />
                {selectedPopup}
            </LayersControl.Overlay>

        </div>
    )
}


export function createSingleMinedexPopupTable(feature, latLng, setClosed) {
    return <Popup latitude={latLng.lat} longitude={latLng.lng}
        anchor="bottom"
        onClose={() => setClosed(false)}><Table striped bordered >
            <Table striped bordered >
                <tbody>
                    <tr>
                        <td>Site Code:</td>
                        <td>{feature.properties.site_code}</td>
                    </tr>
                    <tr>
                        <td>Site Title:</td>
                        <td>{feature.properties.site_title}</td>
                    </tr>
                    <tr>
                        <td>Site Type:</td>
                        <td>{feature.properties.site_type_}</td>
                    </tr>
                    <tr>
                        <td>Site Sub Type:</td>
                        <td>{feature.properties.site_sub_t}</td>
                    </tr>
                    <tr>
                        <td>Site Stage:</td>
                        <td>{feature.properties.site_stage}</td>
                    </tr>
                    <tr>
                        <td>Commodity:</td>
                        <td>{feature.properties.commodity}</td>
                    </tr>
                    <tr>
                        <td>Target Commodity:</td>
                        <td>{feature.properties.target_com}</td>
                    </tr>
                    <tr>
                        <td>Site Commodity:</td>
                        <td>{feature.properties.site_commo}</td>
                    </tr>
                    <tr>
                        <td>Project Title:</td>
                        <td>{feature.properties.proj_title}</td>
                    </tr>
                    <tr>
                        <td>Project Code:</td>
                        <td>{feature.properties.proj_code}</td>
                    </tr>
                    <tr>
                        <td colSpan={2} style={{ textAlign: 'center' }}>
                            <a target="_blank" href={minedexWebLink(feature.properties)}>
                                Minedex Link
                            </a>
                        </td>
                    </tr>


                </tbody>
            </Table>
        </Table>
    </Popup >
}



/*
 * queryMinedexWFSLibre() lived here: a per-viewport WFS GetFeature returning the
 * whole visible set as GeoJSON, filtered in JS afterwards. It was removed when
 * MINEDEX moved to vector tiles - it cost 3.59 MB per pan at the zoom the layer
 * first appeared, and the same again on every filter change.
 *
 * The Leaflet overlay above still has its own equivalent (queryMinedexWFS),
 * because components/Map.js has not been migrated.
 */

export function MinedexOverlayMapLibre({ overlayVisibility, minedexFilters }) {
    // One symbol layer per commodity, generated from the DMIRS symbol table.
    //
    // This replaces eleven hand-written configs that had drifted: SPECIALITY
    // METAL was filtered as 'SPECIALTY METAL' (never matched), industrial and
    // precious mineral were both filtered on 'ALUMINA' (copy-paste - they drew
    // alumina sites twice and their own not at all), and 'OTHER (MINEDEX
    // NON-MINERALS)' had no layer. Deriving them from the table keeps the
    // filter, the icon id and the legend from disagreeing again.
    //
    // The commodity test stays an equality against the icon's own key - that is
    // what selects WHICH symbol to draw, and it is exhaustive because the view
    // coalesces a null commodity into OTHER (MINEDEX NON-MINERALS) server-side.
    // The user's filtering is a separate clause, built to show unrecognised
    // values rather than hide them. See buildMinedexTileFilter.
    const userFilter = useMemo(() => buildMinedexTileFilter(minedexFilters), [minedexFilters]);

    const commodityLayers = useMemo(() => Object.keys(DMIRS_MINEDEX_SYMBOLS).map((commodityId) => ({
        id: minedexLayerId(commodityId),
        source: MINEDEX_TILE_SOURCE,
        'source-layer': MINEDEX_TILE_SOURCE_LAYER,
        type: 'symbol',
        minzoom: MINEDEX_POINT_MINZOOM,
        filter: ['all', ['==', ['get', 'commodity'], commodityId], userFilter],
        layout: {
            'icon-image': minedexIconId(commodityId),
            'icon-allow-overlap': true,
            visibility: overlayVisibility.minedex ? 'visible' : 'none',
        },
    })), [overlayVisibility.minedex, userFilter]);

    // promoteId lifts site_code into the feature id so hover/selection state has
    // something to key on. It is the ONLY stable identity MINEDEX has - gid,
    // __gid and oid all change across upstream publishes, which is why the view
    // does not expose them at all.
    return <Source id={MINEDEX_TILE_SOURCE} type="vector" scheme="tms"
                   tiles={[minedexTileUrl]}
                   minzoom={MINEDEX_POINT_MINZOOM} maxzoom={16}
                   promoteId="site_code">
        {commodityLayers.map((cfg) => <Layer key={cfg.id} {...cfg} />)}
    </Source>
}
